import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import StudentIdCardForm from "./StudentIdCardForm";
import StudentIdCardTable from "./StudentIdCardTable";
import { idCardApi } from "./idCardApi";
import StudentIdCardPreview from "./StudentIdCardPreview";

const StudentIdCardPage: React.FC = () => {
  const academicYearMarch31 = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
    return new Date(year, 2, 31).toISOString().substring(0, 10);
  };
  const [form, setForm] = useState<{ mode: "AUTO" | "MANUAL"; studentId?: string; studentIds?: string[]; photoUrl?: string; studentName?: string; scholarNo?: string; className?: string; sectionName?: string; dob?: string; bloodGroup?: string; validUpto?: string }>({
    mode: "AUTO",
    validUpto: academicYearMarch31(),
  });
  const [rows, setRows] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({ open: false, message: "", severity: "info" });
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<"excel" | "pdf">("excel");
  const [exportDataSource, setExportDataSource] = useState<"" | "AUTO" | "MANUAL">("");

  const load = async () => {
    try {
      const res = await idCardApi.list({});
      console.log("IDCard: list response", res);
      setRows(res);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load ID cards", severity: "error" });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      console.log("IDCard: save payload", form);
      const result = await idCardApi.create(form as any);
      if (form.mode === "AUTO" && Array.isArray((form as any).studentIds) && (form as any).studentIds.length > 1) {
        const created = Number(result?.createdCount || 0);
        const skipped = Number(result?.skippedCount || 0);
        const notFound = Number(result?.notFoundCount || 0);
        setSnackbar({
          open: true,
          message: `Created ${created} ID card(s), skipped ${skipped}, not found ${notFound}`,
          severity: "success",
        });
      } else {
        setSnackbar({ open: true, message: "ID Card saved", severity: "success" });
      }
      setForm({ mode: "AUTO", validUpto: academicYearMarch31(), studentIds: [] } as any);
      load();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to save", severity: "error" });
    }
  };

  const edit = async (row: any) => {
    setForm({
      mode: "MANUAL",
      studentId: row.studentId || undefined,
      photoUrl: row.photoUrl || undefined,
      studentName: row.studentName,
      scholarNo: row.scholarNo,
      className: row.className,
      sectionName: row.sectionName,
      dob: row.dob ? new Date(row.dob).toISOString().substring(0, 10) : undefined,
      bloodGroup: row.bloodGroup || undefined,
      validUpto: row.validUpto ? new Date(row.validUpto).toISOString().substring(0, 10) : undefined,
    });
  };

  const remove = async (row: any) => {
    try {
      await idCardApi.delete(row.id);
      setSnackbar({ open: true, message: "Deleted", severity: "success" });
      load();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to delete", severity: "error" });
    }
  };

  const exportData = async () => {
    try {
      const params = exportDataSource ? { dataSource: exportDataSource } : {};
      const res =
        exportFormat === "excel"
          ? await idCardApi.export(params)
          : await idCardApi.exportPdf(params);
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFormat === "excel" ? "id-cards.xlsx" : "id-cards.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
      setSnackbar({
        open: true,
        message: `Exported ${exportFormat.toUpperCase()}${exportDataSource ? ` (${exportDataSource})` : ""}`,
        severity: "success",
      });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to export", severity: "error" });
    }
  };

  const print = (row: any) => {
    idCardApi.pdf(row.id).then((res) => {
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      let filename = "id-card.pdf";
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      if (cd) {
        const m = cd.match(/filename=\"?([^\";]+)\"?/i);
        if (m && m[1]) filename = m[1];
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }).catch((e: any) => {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to print", severity: "error" });
    });
  };

  const preview = async (row: any) => {
    try {
      const res = await idCardApi.preview(row.id);
      setPreviewHtml(res.html);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load preview", severity: "error" });
    }
  };

  const generateFilteredPdf = async (filters: {
    sessionId?: string;
    classId?: string;
    sectionId?: string;
    search?: string;
  }) => {
    try {
      const res = await idCardApi.generateFilteredPdf({
        ...filters,
        validUpto: form.validUpto,
      });
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      let filename = "id-cards-filtered.pdf";
      const cd =
        res.headers?.["content-disposition"] ||
        res.headers?.["Content-Disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m && m[1]) filename = m[1];
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      const generated = Number(res.headers?.["x-idcards-generated"] || 0);
      const existing = Number(res.headers?.["x-idcards-existing"] || 0);
      const total = Number(res.headers?.["x-idcards-total"] || generated + existing);
      setSnackbar({
        open: true,
        message: `Generated PDF for ${total} student(s): new ${generated}, existing ${existing}`,
        severity: "success",
      });

      load();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || "Failed to generate filtered PDF",
        severity: "error",
      });
    }
  };

  const generateFilteredA4Pdf = async (filters: {
    sessionId?: string;
    classId?: string;
    sectionId?: string;
    search?: string;
  }) => {
    try {
      const res = await idCardApi.generateFilteredA4Pdf({
        ...filters,
        validUpto: form.validUpto,
      });
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      let filename = "id-cards-a4.pdf";
      const cd =
        res.headers?.["content-disposition"] ||
        res.headers?.["Content-Disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m && m[1]) filename = m[1];
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      const generated = Number(res.headers?.["x-idcards-generated"] || 0);
      const existing = Number(res.headers?.["x-idcards-existing"] || 0);
      const total = Number(res.headers?.["x-idcards-total"] || generated + existing);
      setSnackbar({
        open: true,
        message: `A4 bulk PDF generated for ${total} student(s): new ${generated}, existing ${existing}`,
        severity: "success",
      });
      load();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || "Failed to generate A4 bulk PDF",
        severity: "error",
      });
    }
  };

  const generateCorrectionCheckPdf = async (filters: {
    sessionId?: string;
    classId?: string;
    sectionId?: string;
    search?: string;
  }) => {
    try {
      const res = await idCardApi.generateCorrectionCheckPdf({
        ...filters,
        validUpto: form.validUpto,
      });
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      let filename = "id-cards-correction-check.pdf";
      const cd =
        res.headers?.["content-disposition"] ||
        res.headers?.["Content-Disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/i);
        if (m && m[1]) filename = m[1];
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: "Correction check PDF generated",
        severity: "success",
      });
      load();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || e?.message || "Failed to generate correction check PDF",
        severity: "error",
      });
    }
  };

  const bulkManualUpload = async (file: File) => {
    try {
      const result = await idCardApi.bulkUploadManual(file, form.validUpto);
      setSnackbar({
        open: true,
        message: `Bulk import complete: created ${result.createdCount || 0}, skipped ${result.skippedCount || 0}, failed ${result.failedCount || 0}`,
        severity: "success",
      });
      load();
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || e?.message || "Bulk upload failed",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>Student ID Card</Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <StudentIdCardForm
          form={form}
          onChange={setForm}
          onSubmit={save}
          // onGenerateFilteredPdf={generateFilteredPdf}
          // onGenerateFilteredA4Pdf={generateFilteredA4Pdf}
          // onGenerateCorrectionCheckPdf={generateCorrectionCheckPdf}
          // onBulkManualUpload={bulkManualUpload}
        /> 
      </Paper>
      <Paper sx={{ p: 2 }}>
        <StudentIdCardTable
          rows={rows}
          onEdit={edit}
          onDelete={remove}
          onExport={() => setExportOpen(true)}
          onPrint={print}
          onPreview={preview}
        />
      </Paper>
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export ID Cards</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="export-format-label">Format</InputLabel>
              <Select
                labelId="export-format-label"
                label="Format"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as "excel" | "pdf")}
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="export-source-label">Data Source</InputLabel>
              <Select
                labelId="export-source-label"
                label="Data Source"
                value={exportDataSource}
                onChange={(e) => setExportDataSource(e.target.value as "" | "AUTO" | "MANUAL")}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="AUTO">Auto</MenuItem>
                <MenuItem value="MANUAL">Manual</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={exportData}>Export</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!previewHtml} onClose={() => setPreviewHtml(null)} maxWidth="md" fullWidth>
        <DialogTitle>ID Card Preview</DialogTitle>
        <DialogContent dividers>
          {previewHtml && <StudentIdCardPreview html={previewHtml} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewHtml(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentIdCardPage;
